// ABOUTME: Bridges react-router navigation to non-React call sites (Apollo error link, useMe).
// ABOUTME: Centralizes the "send an unauthenticated user to login" decision behind the in-app-auth flag.

import type { NavigateFunction } from "react-router"
import config from "../config"
import { disableInAppAuth, isInAppAuthEnabled } from "@/model/auth/inAppAuthFlag"
import { AUTH_PATH_PREFIX, LOGIN_PAGE } from "@shared/constants/navigation"

// react-router's navigate() is only available inside the router tree. The Apollo
// error link and useMe live outside (or above) it and need to trigger navigation,
// so RouterBridge publishes the current navigate() here for them to use.
let navigateFn: NavigateFunction | null = null

export const setNavigate = (fn: NavigateFunction | null): void => {
  navigateFn = fn
}

export const isOnAuthRoute = (pathname: string = window.location.pathname): boolean =>
  pathname === AUTH_PATH_PREFIX || pathname.startsWith(`${AUTH_PATH_PREFIX}/`)

// Loop breaker: if the app keeps sending the user to login and login keeps bouncing them
// back (Kratos session valid, but the app API still returns access-denied — e.g. local dev
// where the API needs the Bearer token, not just the cookie), we would ping-pong forever
// between "/" and "/auth/login". Count redirects in a short window; past the threshold the
// caller degrades to the external flow.
const LOOP_KEY = "inapp_auth_login_bounces"
const LOOP_WINDOW_MS = 8000
const LOOP_MAX = 3

const bumpAndDetectLoop = (): boolean => {
  try {
    const now = Date.now()
    const raw = window.sessionStorage.getItem(LOOP_KEY)
    const recent = ((raw ? JSON.parse(raw) : []) as number[]).filter((t) => now - t < LOOP_WINDOW_MS)
    recent.push(now)
    window.sessionStorage.setItem(LOOP_KEY, JSON.stringify(recent))
    return recent.length > LOOP_MAX
  } catch {
    return false
  }
}

/**
 * Redirect an unauthenticated user to login.
 * - in-app-auth flag ON  → SPA-navigate to the internal /auth/login (no domain jump)
 * - flag OFF (default)   → the existing external redirect to auth.trainingendurance.com
 * No-ops when already on an /auth/* route, so an access-denied fired while the login
 * screen itself is mounting cannot cause a redirect loop.
 */
export const redirectToLogin = (): void => {
  if (isOnAuthRoute()) return

  if (isInAppAuthEnabled() && navigateFn) {
    // In-app login is bouncing back repeatedly → disable it for this session and fall back to
    // the external flow (which mints the Bearer token the API needs), instead of looping. The
    // loop counter is scoped to the in-app path so the external path never touches it.
    if (bumpAndDetectLoop()) {
      disableInAppAuth()
      window.location.href = config.linkToLogin()
      return
    }
    const returnTo = window.location.pathname + window.location.search
    navigateFn(`${LOGIN_PAGE}?return_to=${encodeURIComponent(returnTo)}`, { replace: true })
    return
  }

  window.location.href = config.linkToLogin()
}
